import { createSlice } from '@reduxjs/toolkit';

import { LocalStorage } from 'services';
import { ClientDetails } from 'types';
import { authApi } from './authentication.actions';
import { clearSessionToken, eraseCookie, getSessionToken, setSessionToken, setLastUsername, getLastUsername, clearLastUsername } from '../../utils/cookies';
import { jsonParse } from '../../utils/common';

const clientData = LocalStorage.get('clientData');

export interface AuthState {
    isLogged: boolean
    clientDetails: ClientDetails,
    token: string,
    lastUsername: string,
    checksum: string | null
}

export const authFromSession: any = jsonParse(getSessionToken('auth') || '{}');

const initialState: AuthState = {
    isLogged: !!authFromSession.token,
    clientDetails: clientData || {} as ClientDetails,
    token: authFromSession.token || '',
    lastUsername: getLastUsername() || '',
    checksum: LocalStorage.get('CHECKSUM') || null
};

const authenticationSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        clearAuth: (state, action) => {
            state.isLogged = false;
            state.token = '';
            const clearDeviceData = action.payload?.clearDeviceData;
            if (clearDeviceData) {
                state.clientDetails = {} as ClientDetails;
                LocalStorage.remove('DEVICE_ID');
                LocalStorage.remove('LOGIN_OTP_FLOW');
                LocalStorage.remove('clientData');
            }
            if (clearDeviceData) {
                state.checksum = null;
                LocalStorage.remove('CHECKSUM');
            }
            eraseCookie('auth');
            clearSessionToken('auth');
        },
        syncWithSessionStorage: (state) => {
            const sessionAuth = jsonParse(getSessionToken('auth') || '{}');
            const localUsername = getLastUsername();
            const savedChecksum = LocalStorage.get('CHECKSUM');
            
            if (!sessionAuth.token) {
                state.isLogged = false;
                state.token = '';
                state.checksum = null;
            } else {
                state.token = sessionAuth.token;
                state.isLogged = true;
            }
            
            if (localUsername && localUsername.trim() !== '' && localUsername !== 'undefined') {
                state.lastUsername = localUsername;
            }
            
            if (savedChecksum) {
                state.checksum = savedChecksum;
            }
        },
        activateTokenAfterOTP: (state, action) => {
            const { token, username, checksum } = action.payload;
            
            if (username && username.trim() !== '' && username !== 'undefined') {
                const authData = { token, lastUsername: username };
                setSessionToken('auth', JSON.stringify(authData));
                setLastUsername(username);
                state.lastUsername = username;
            } else {
                const authData = { token };
                setSessionToken('auth', JSON.stringify(authData));
            }
            
            if (checksum) {
                state.checksum = checksum;
                LocalStorage.set('CHECKSUM', checksum);
            }
            
            state.isLogged = true;
            state.token = token;
        },
    },
    extraReducers: (builder) => {
        builder.addMatcher(authApi.endpoints.getAuthToken.matchFulfilled,
            (state, action) => {
                const { token, client } = action.payload;
                const username = action.meta.arg.originalArgs.username;
                
                if (username && username.trim() !== '' && username !== 'undefined') {
                    const authData = { token, lastUsername: username };
                    setSessionToken('auth', JSON.stringify(authData));
                    setLastUsername(username);
                    state.lastUsername = username;
                } else {
                    const authData = { token };
                    setSessionToken('auth', JSON.stringify(authData));
                }
                
                if (client?.email) {
                    state.clientDetails.email = client.email;
                }
                
                state.isLogged = true;
                state.token = token;
            });
        builder.addMatcher(authApi.endpoints.getAuthToken.matchRejected, (state) => {
            state.isLogged = false;
        });
        builder.addMatcher(authApi.endpoints.logout.matchFulfilled,
            (state, action) => {
                state.isLogged = false;
                state.token = '';
                const clearDeviceData = action.meta.arg.originalArgs?.clearDeviceData;
                if (clearDeviceData) {
                    state.clientDetails = {} as ClientDetails;
                    clearLastUsername();
                    LocalStorage.remove('DEVICE_ID');
                    LocalStorage.remove('LOGIN_OTP_FLOW');
                    LocalStorage.remove('clientData');
                }
                if (clearDeviceData) {
                    state.checksum = null;
                    LocalStorage.remove('CHECKSUM');
                }
                eraseCookie('auth');
                clearSessionToken('auth');
            });
        builder.addMatcher(authApi.endpoints.logout.matchRejected, (state, action) => {
            state.isLogged = false;
            state.token = '';
            const clearDeviceData = action.meta.arg.originalArgs?.clearDeviceData;
            if (clearDeviceData) {
                state.clientDetails = {} as ClientDetails;
                clearLastUsername();
                LocalStorage.remove('DEVICE_ID');
                LocalStorage.remove('LOGIN_OTP_FLOW');
                LocalStorage.remove('clientData');
            }
            if (clearDeviceData) {
                state.checksum = null;
                LocalStorage.remove('CHECKSUM');
            }
            eraseCookie('auth');
            clearSessionToken('auth');
        });
    },
});

export const { clearAuth, syncWithSessionStorage, activateTokenAfterOTP } = authenticationSlice.actions;

export default authenticationSlice.reducer;
